import { useCallback, useState } from 'react';
import {
  AnyVariables,
  DocumentInput,
  OperationContext,
  OperationResult,
  useClient,
  UseQueryState,
} from 'urql';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';

// This wrapper over useQuery indicates whether or not we are waiting for
// the project provider to mount via the fetching attribute
export const useLazyQuery = <Data = any, Variables extends AnyVariables = AnyVariables>(
  query: DocumentInput<Data, Variables>,
): [
  UseQueryState<Data, Variables>,
  (variables: Variables) => Promise<OperationResult<Data, Variables>>,
] => {
  const client = useClient();
  const { isInContext, isMounted } = useDbtCloudProject();

  const [result, setResult] = useState<UseQueryState<Data, Variables>>({
    fetching: false,
    data: undefined,
    error: undefined,
    stale: false,
  });

  const resultPromise = useCallback(
    (vars: Variables, context?: Partial<OperationContext>) => {
      setResult((prev) => ({ ...prev, fetching: true }));
      const request = client.query(query, vars, context);
      const result = request.toPromise();
      result.then((result) => {
        setResult((prev) => ({
          ...prev,
          fetching: false,
          stale: result.stale,
          data: result.data,
          error: result.error,
        }));
      });
      return result;
    },
    [client, query],
  );

  // We check both !isMounted and isInContext because only in project-level pages
  // do we want to indicate fetching=True when an environment id is not present.
  // Account level pages are not subject to this condition.
  return [
    { ...result, fetching: result.fetching || (!isMounted && isInContext) },
    resultPromise,
  ];
};
